import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner } from "reactstrap";
import DataTable from "react-data-table-component";
import { Add, ViewPackageTitle, PackageTitle } from "../../../Utils/Constants";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl, mediaUrl } from "../../../Utils/FileUpload";
import { Package } from "../../../Types/Tables.type";
import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn } from "../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { GeneralSetting } from "../../../Types/Tables.type";

export default function ViewPackage() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Package[]>([]);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [disabledDelete, setDisabledDelete] = useState<string | null>(null);
  const [disableAllDelete, setDisableAllDelete] = useState(false);
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();

  const [baseValues, setBaseValues] = useState({
    base_amount: 0,
    base_customer_credit_point: 0,
    base_call_duration: 0,
    base_agent_point: 0,
    base_agent_payment: 0,
  });
  const [disabledEnable, setDisabledEnable] = useState<string | null>(null);
  const [disableAllEnable, setDisableAllEnable] = useState(false);
  // const [currency, setCurrency] = useState("");
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");

        // Fetch packages and base values in parallel
        const [packageRes, baseRes] = await Promise.all([
          axios.get(`${baseUrl}package/getall`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${baseUrl}package_payment_setting/getall`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const packages: Package[] = packageRes.data;
        const baseData = baseRes.data[0];

        const baseValues = {
          base_amount: parseFloat(baseData.base_amount),
          base_customer_credit_point: parseFloat(
            baseData.base_customer_credit_point
          ),
          base_call_duration: parseFloat(baseData.base_call_duration),
          base_agent_point: parseFloat(baseData.base_agent_point),
          base_agent_payment: parseFloat(baseData.base_agent_payment),
        };

        const updatedPackages = packages.map((pkg) => {
          const inputAmount =
            typeof pkg.amount === "string"
              ? parseFloat(pkg.amount)
              : pkg.amount;
          const baseAmount = baseValues.base_amount;

          return {
            ...pkg,
            base_customer_credit_point: parseFloat(
              (
                (inputAmount / baseAmount) *
                baseValues.base_customer_credit_point
              ).toFixed(2)
            ),
            base_call_duration: parseFloat(
              (
                (inputAmount / baseAmount) *
                baseValues.base_call_duration
              ).toFixed(2)
            ),
            base_agent_point: parseFloat(
              (
                (inputAmount / baseAmount) *
                baseValues.base_agent_point
              ).toFixed(2)
            ),
            base_agent_payment: parseFloat(
              (
                (inputAmount / baseAmount) *
                baseValues.base_agent_payment
              ).toFixed(2)
            ),
          };
        });

        setBaseValues(baseValues);
        setFilteredItems(updatedPackages);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [refresh]);

  const handleAdd = () => {
    navigate("/add_package");
  };

  const filteredData = filteredItems.filter((item) => {
    return (
      (item.name &&
        item.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.image &&
        item.image.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.amount &&
        item.amount
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.country?.name &&
        item.country?.name.toLowerCase().includes(filterText.toLowerCase())) ||
      // ✅ Include calculated fields
      (item.base_customer_credit_point &&
        item.base_customer_credit_point
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.base_call_duration &&
        item.base_call_duration
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.base_agent_point &&
        item.base_agent_point
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase())) ||
      (item.base_agent_payment &&
        item.base_agent_payment
          .toString()
          .toLowerCase()
          .includes(filterText.toLowerCase()))
    );
  });
  const handleEdit = (id: string) => {
    navigate("/edit_package", { state: { id: id } });
  };

  const handleDelete = (id: string) => {
    setDisabledDelete(id);
    setDisableAllDelete(true);
    const confirmToast = () =>
      toast.warn(
        <div>
          <div>Are you sure you want to delete this package?</div>
          <div className="d-flex justify-content-end mt-2">
            <Button color="danger" onClick={() => confirmDelete(id)}>
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledDelete(null);
                setDisableAllDelete(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisableAllDelete(false);
            setDisabledDelete(null);
          },
        }
      );

    const confirmDelete = async (id: string) => {
      setDisabledDelete(null);
      setDisableAllDelete(false);
      toast.dismiss();
      try {
        await axios.post(
          `${baseUrl}package/delete`,
          { id },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`Package Deleted Successfully`, {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
        setRefresh((prev) => prev + 1);
      } catch (error) {
        console.error("Error deleting package:", error);
        toast.error("An error occurred while deleting the package", {
          position: "top-right",
          autoClose: 1000,
          closeOnClick: true,
        });
      }
    };

    confirmToast();
  };

  const toggleEnable = (id: string, currentStatus: number) => {
    setDisabledEnable(id); // reuse this state if needed
    setDisableAllEnable(true);

    const confirmToggle = () => {
      toast.warn(
        <div>
          <div>
            Are you sure you want to{" "}
            <strong>{currentStatus === 1 ? "disable" : "enable"}</strong> this
            package?
          </div>
          <div className="d-flex justify-content-end mt-2">
            <Button
              color={currentStatus === 1 ? "danger" : "success"}
              onClick={() => confirmToggleEnable(id, currentStatus)}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                setDisabledEnable(null);
                setDisableAllEnable(false);
                toast.dismiss();
              }}
              className="ms-2"
            >
              Cancel
            </Button>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: false,
          closeOnClick: false,
          closeButton: false,
          draggable: false,
          onClose: () => {
            setDisabledEnable(null);
            setDisableAllEnable(false);
          },
        }
      );
    };

    const confirmToggleEnable = async (id: string, currentStatus: number) => {
      setDisabledEnable(null);
      setDisableAllEnable(false);
      toast.dismiss();

      try {
        const newStatus = currentStatus === 1 ? 0 : 1;

        await axios.post(
          `${baseUrl}package/toggle_active`,
          { id, is_active: newStatus },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        toast.success(
          `Package ${newStatus === 1 ? "enabled" : "disabled"} successfully!`,
          {
            position: "top-right",
            autoClose: 1000,
            closeOnClick: true,
            onClose: () => {
              setRefresh((prev) => prev + 1);
            },
          }
        );
      } catch (error) {
        console.error("Error toggling package:", error);
        toast.error("Error updating enable status", {
          position: "top-right",
          autoClose: 1000,
        });
      }
    };

    confirmToggle();
  };
  // Define columns with actions
  const packageTableColumns = [
    {
      name: "Image",
      cell: (row: Package) => (
        <img
          src={`${mediaUrl}/${row.image}`}
          alt={row.image}
          width={25}
          height={25}
          style={{
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      ),
      // sortable: true,
      width: "7%",
    },
    {
      name: "Package Name",
      selector: (row: Package) => row.name,
      sortable: true,
      width: "12%",
    },
    {
      name: "Recharge Amount",
      selector: (row: Package) =>
        `${row.amount}${currency ? ` ${currency}` : ""}`,
      width: "14%",
      sortable: true,
    },
    {
      name: "Customer Points",
      selector: (row: Package) => row.base_customer_credit_point,
      sortable: true,
      width: "13%",
    },
    {
      name: "Call Duration (mins)",
      selector: (row: Package) => row.base_call_duration,
      sortable: true,
      width: "11%",
    },
    {
      name: "Agent Points",
      selector: (row: Package) => row.base_agent_point,
      sortable: true,
      width: "11%",
    },
    {
      name: "Agent Payment",
      selector: (row: Package) =>
        `${row.base_agent_payment}${currency ? ` ${currency}` : ""}`,
      sortable: true,
      width: "12%",
    },
    {
      name: "Country",
      selector: (row: Package) => row.country?.name,
      sortable: true,
      width: "10%",
      wrap: true,
    },
    {
      name: "Actions",
      cell: (row: Package) => (
        <div className="d-flex justify-content-between">
          <Button
            color={row.is_active ? "success" : "secondary"}
            size="sm"
            onClick={() => toggleEnable(row.id, row.is_active)}
            disabled={disabledEnable === row.id || disableAllEnable}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={row.is_active ? "Disable" : "Enable"}
          >
            <i
              className={`fa ${row.is_active ? "fa-check-circle" : "fa-ban"}`}
            />
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() => handleEdit(row.id)}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Edit"
          >
            <i className="icon-pencil-alt" />
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
            disabled={disabledDelete === row.id || disableAllDelete}
            style={{
              marginRight: "5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Delete"
          >
            <i className="icon-trash" />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      // button: true,
      width: "10%",
    },
  ];

  // Show a loading spinner while data is being fetched
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };
  return (
    <>
      <Breadcrumbs
        pageTitle={ViewPackageTitle}
        parent={PackageTitle}
        title={ViewPackageTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
              <Btn color="primary" onClick={handleAdd}>
                {Add}
              </Btn>
            </Col>
            <div className="mb-3">
              <FilterComponent
                onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilterText(e.target.value)
                }
                filterText={filterText}
              />
            </div>
            <div className="table-responsive">
              <DataTable
                data={filteredData}
                columns={packageTableColumns}
                pagination
                highlightOnHover
                striped
                customStyles={customStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer />
    </>
  );
}
